
import { useState } from 'react';
import Button from '../base/Button';

interface UploadedImage {
  id: string;
  name: string;
  url: string;
  roomType?: string;
  file_size: number;
  file_type: string;
}

interface AIStagingModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadedImages?: UploadedImage[];
  preselectedStyle?: string;
}

interface StagingStyle {
  id: string;
  name: string;
  description: string;
  icon: string;
  preview: string;
}

interface FurnitureSet {
  sofa: string;
  coffeeTable: string;
  rug: string;
  decor: string;
}

export default function AIStagingModal({ isOpen, onClose, uploadedImages = [], preselectedStyle = 'modern' }: AIStagingModalProps) {
  const [selectedStyle, setSelectedStyle] = useState<string>(preselectedStyle);
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<'style' | 'rooms' | 'processing' | 'results'>('style');
  const [stagedResults, setStagedResults] = useState<Record<string, string[]>>({});
  const [processingStatus, setProcessingStatus] = useState<string>('');

  const stagingStyles: StagingStyle[] = [
    {
      id: 'modern',
      name: 'Modern Minimalist',
      description: 'Clean lines, neutral colors, contemporary furniture with open spaces',
      icon: 'ri-home-4-line',
      preview: 'https://public.readdy.ai/ai/img_res/b17a069716617ee0a0a6e9769256be2e.jpg'
    },
    {
      id: 'traditional',
      name: 'Traditional Comfort',
      description: 'Classic furniture, warm colors, cozy atmosphere with timeless appeal',
      icon: 'ri-plant-line',
      preview: 'https://readdy.ai/api/search-image?query=traditional%20comfortable%20living%20room%20with%20classic%20furniture%20warm%20colors%20cozy%20atmosphere%20timeless%20appeal%20elegant%20design&width=300&height=200&seq=traditional-staging&orientation=landscape'
    },
    {
      id: 'luxury',
      name: 'Luxury Elite',
      description: 'High-end furniture, premium materials, sophisticated elegant design',
      icon: 'ri-palette-line',
      preview: 'https://readdy.ai/api/search-image?query=luxury%20elite%20living%20room%20with%20high-end%20furniture%20premium%20materials%20sophisticated%20elegant%20design%20expensive%20decor&width=300&height=200&seq=luxury-staging&orientation=landscape'
    },
    {
      id: 'scandinavian',
      name: 'Scandinavian Style',
      description: 'Light woods, white walls, hygge comfort with natural textures',
      icon: 'ri-leaf-line',
      preview: 'https://readdy.ai/api/search-image?query=scandinavian%20style%20living%20room%20with%20light%20wood%20white%20walls%20hygge%20comfort%20natural%20textures%20minimalist%20nordic%20design&width=300&height=200&seq=scandinavian-staging&orientation=landscape'
    },
    {
      id: 'industrial',
      name: 'Industrial Chic',
      description: 'Exposed brick, metal accents, urban loft aesthetic with character',
      icon: 'ri-building-line',
      preview: 'https://readdy.ai/api/search-image?query=industrial%20chic%20living%20room%20with%20exposed%20brick%20metal%20accents%20urban%20loft%20aesthetic%20character%20modern%20industrial%20design&width=300&height=200&seq=industrial-staging&orientation=landscape'
    },
    {
      id: 'bohemian',
      name: 'Bohemian Eclectic',
      description: 'Colorful textiles, mixed patterns, artistic creative vibrant atmosphere',
      icon: 'ri-brush-line',
      preview: 'https://readdy.ai/api/search-image?query=bohemian%20eclectic%20living%20room%20with%20colorful%20textiles%20mixed%20patterns%20artistic%20creative%20vibrant%20atmosphere%20boho%20style&width=300&height=200&seq=bohemian-staging&orientation=landscape'
    }
  ];

  const handleRoomToggle = (roomId: string) => {
    setSelectedRooms(prev => 
      prev.includes(roomId) 
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId]
    );
  };

  // Step 1: Generate furniture and rug images for the selected style
  const generateFurnitureImages = async (style: string, optionIndex: number): Promise<FurnitureSet> => {
    const timestamp = Date.now();
    const styleConfigs = {
      modern: {
        sofa: 'modern minimalist gray sectional sofa with clean lines contemporary design neutral fabric sleek legs isolated white background furniture product photography',
        coffeeTable: 'modern glass coffee table with metal legs minimalist design contemporary furniture isolated white background product photography',
        rug: 'modern geometric area rug neutral colors gray white pattern minimalist design contemporary home decor isolated white background',
        decor: 'modern minimalist wall art abstract geometric prints contemporary home decor accessories isolated white background'
      },
      traditional: {
        sofa: 'traditional classic brown leather sofa with rolled arms button tufting elegant design comfortable furniture isolated white background product photography',
        coffeeTable: 'traditional wooden coffee table with carved details classic design dark wood finish elegant furniture isolated white background product photography',
        rug: 'traditional persian area rug with intricate patterns warm colors red gold classic design elegant home decor isolated white background',
        decor: 'traditional classic table lamps with fabric shades elegant home accessories warm lighting isolated white background product photography'
      },
      luxury: {
        sofa: 'luxury velvet sofa with gold accents elegant design premium materials sophisticated furniture high-end interior isolated white background product photography',
        coffeeTable: 'luxury marble coffee table with gold base premium materials elegant design sophisticated furniture isolated white background product photography',
        rug: 'luxury silk area rug with elegant patterns premium quality sophisticated design high-end home decor isolated white background',
        decor: 'luxury crystal chandelier with gold accents premium lighting elegant design sophisticated home accessories isolated white background'
      },
      scandinavian: {
        sofa: 'scandinavian light wood frame sofa with white cushions minimalist design nordic style natural materials cozy furniture isolated white background product photography',
        coffeeTable: 'scandinavian light wood coffee table simple design natural materials nordic style minimalist furniture isolated white background product photography',
        rug: 'scandinavian wool area rug natural colors simple patterns nordic design cozy home decor isolated white background',
        decor: 'scandinavian wooden accessories natural materials simple design nordic style cozy home decor isolated white background'
      },
      industrial: {
        sofa: 'industrial leather sofa with metal frame urban design loft style dark colors modern furniture isolated white background product photography',
        coffeeTable: 'industrial metal coffee table with wood top urban design loft style modern furniture isolated white background product photography',
        rug: 'industrial style area rug dark colors urban design loft aesthetic modern home decor isolated white background',
        decor: 'industrial metal wall art urban design loft style modern home accessories isolated white background'
      },
      bohemian: {
        sofa: 'bohemian colorful sofa with mixed patterns eclectic design vibrant textiles artistic furniture isolated white background product photography',
        coffeeTable: 'bohemian wooden coffee table with carved details eclectic design artistic furniture colorful accents isolated white background product photography',
        rug: 'bohemian colorful area rug with intricate patterns vibrant colors eclectic design artistic home decor isolated white background',
        decor: 'bohemian colorful wall hangings tapestries artistic home decor vibrant patterns eclectic design isolated white background'
      }
    };

    const config = styleConfigs[style as keyof typeof styleConfigs] || styleConfigs.modern;
    
    // Generate different variations for each option
    const variations = [
      { suffix: 'elegant sophisticated premium quality', brightness: 1.1 },
      { suffix: 'cozy comfortable warm atmosphere', brightness: 1.0 },
      { suffix: 'bright airy spacious modern', brightness: 1.15 }
    ];

    const variation = variations[optionIndex] || variations[0];

    // Add error testing and validation
    console.log('🔧 DEBUG: Generating furniture images for style:', style, 'option:', optionIndex);
    
    const furnitureSet = {
      sofa: `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28config.sofa%20%20%20%20%20%20%20variation.suffix%29%7D&width=400&height=300&seq=sofa-${style}-${optionIndex}-${timestamp}&orientation=landscape`,
      coffeeTable: `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28config.coffeeTable%20%20%20%20%20%20%20variation.suffix%29%7D&width=300&height=200&seq=table-${style}-${optionIndex}-${timestamp}&orientation=landscape`,
      rug: `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28config.rug%20%20%20%20%20%20%20variation.suffix%29%7D&width=500&height=300&seq=rug-${style}-${optionIndex}-${timestamp}&orientation=landscape`,
      decor: `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28config.decor%20%20%20%20%20%20%20variation.suffix%29%7D&width=200&height=200&seq=decor-${style}-${optionIndex}-${timestamp}&orientation=squarish`
    };

    console.log('🔧 DEBUG: Generated furniture URLs:', furnitureSet);
    
    // Test if URLs are accessible
    try {
      const testResponse = await fetch(furnitureSet.sofa, { method: 'HEAD' });
      console.log('🔧 DEBUG: Sofa image URL test response:', testResponse.status, testResponse.statusText);
    } catch (error) {
      console.error('🔧 DEBUG: Error testing sofa URL:', error);
    }

    return furnitureSet;
  };

  // Step 2: Composite furniture into the room image
  const compositeFurnitureIntoRoom = async (
    originalImageUrl: string, 
    furnitureSet: FurnitureSet, 
    optionIndex: number
  ): Promise<string> => {
    console.log('🔧 DEBUG: Starting composite for option:', optionIndex);
    console.log('🔧 DEBUG: Original image URL:', originalImageUrl);
    console.log('🔧 DEBUG: Furniture set:', furnitureSet);

    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.error('🔧 DEBUG: Canvas context not available');
        resolve(originalImageUrl);
        return;
      }

      const roomImage = new Image();
      roomImage.crossOrigin = 'anonymous';
      
      roomImage.onload = async () => {
        console.log('🔧 DEBUG: Room image loaded successfully:', roomImage.width, 'x', roomImage.height);
        
        // Set canvas size to match room image
        canvas.width = roomImage.width;
        canvas.height = roomImage.height;
        
        // Draw the original room image
        ctx.drawImage(roomImage, 0, 0);
        console.log('🔧 DEBUG: Room image drawn to canvas');
        
        try {
          // Test furniture image loading
          const furnitureLoadTests = await Promise.allSettled([
            testImageLoad(furnitureSet.rug),
            testImageLoad(furnitureSet.sofa),
            testImageLoad(furnitureSet.coffeeTable),
            testImageLoad(furnitureSet.decor)
          ]);
          
          console.log('🔧 DEBUG: Furniture load test results:', furnitureLoadTests);
          
          // Load and composite furniture images
          await Promise.all([
            loadAndCompositeImage(ctx, furnitureSet.rug, canvas.width, canvas.height, 'rug', optionIndex),
            loadAndCompositeImage(ctx, furnitureSet.sofa, canvas.width, canvas.height, 'sofa', optionIndex),
            loadAndCompositeImage(ctx, furnitureSet.coffeeTable, canvas.width, canvas.height, 'table', optionIndex),
            loadAndCompositeImage(ctx, furnitureSet.decor, canvas.width, canvas.height, 'decor', optionIndex)
          ]);
          
          console.log('🔧 DEBUG: All furniture composited');
          
          // Add staging watermark
          ctx.font = 'bold 16px Arial';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
          ctx.lineWidth = 2;
          ctx.strokeText('AI Staged', canvas.width - 120, canvas.height - 20);
          ctx.fillText('AI Staged', canvas.width - 120, canvas.height - 20);
          
          // Convert to blob and create download URL
          canvas.toBlob((blob) => {
            if (blob) {
              console.log('🔧 DEBUG: Blob created successfully, size:', blob.size, 'bytes');
              const url = URL.createObjectURL(blob);
              console.log('🔧 DEBUG: Blob URL created:', url);
              resolve(url);
            } else {
              console.error('🔧 DEBUG: Failed to create blob');
              resolve(originalImageUrl);
            }
          }, 'image/jpeg', 0.92);
          
        } catch (error) {
          console.error('🔧 DEBUG: Error compositing furniture:', error);
          resolve(originalImageUrl);
        }
      };
      
      roomImage.onerror = (error) => {
        console.error('🔧 DEBUG: Room image failed to load:', error);
        resolve(originalImageUrl);
      };
      
      roomImage.src = originalImageUrl;
    });
  };

  // Helper function to test image loading
  const testImageLoad = (imageUrl: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        console.log('🔧 DEBUG: Image loaded successfully:', imageUrl);
        resolve(true);
      };
      
      img.onerror = (error) => {
        console.error('🔧 DEBUG: Image failed to load:', imageUrl, error);
        resolve(false);
      };
      
      img.src = imageUrl;
    });
  };

  // Helper function to load and composite individual furniture pieces
  const loadAndCompositeImage = (
    ctx: CanvasRenderingContext2D,
    imageUrl: string,
    canvasWidth: number,
    canvasHeight: number,
    type: 'rug' | 'sofa' | 'table' | 'decor',
    optionIndex: number
  ): Promise<void> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        console.log(`🔧 DEBUG: ${type} image loaded:`, img.width, 'x', img.height);
        
        // Calculate positioning based on furniture type and option
        const positions = getFurniturePositions(type, optionIndex, canvasWidth, canvasHeight);
        console.log(`🔧 DEBUG: ${type} position:`, positions);
        
        // Apply blend mode for realistic integration
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = 0.8;
        
        // Draw furniture piece
        ctx.drawImage(
          img,
          positions.x,
          positions.y,
          positions.width,
          positions.height
        );
        
        // Reset composite operation
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1.0;
        
        console.log(`🔧 DEBUG: ${type} composited successfully`);
        resolve();
      };
      
      img.onerror = (error) => {
        console.error(`🔧 DEBUG: ${type} image failed to load:`, imageUrl, error);
        resolve(); // Continue even if one piece fails
      };
      
      img.src = imageUrl;
    });
  };

  // Calculate furniture positions based on room layout and option
  const getFurniturePositions = (
    type: 'rug' | 'sofa' | 'table' | 'decor',
    optionIndex: number,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    
    const layouts = [
      // Option 1: Centered layout
      {
        rug: { x: centerX - 200, y: centerY + 50, width: 400, height: 200 },
        sofa: { x: centerX - 150, y: centerY - 50, width: 300, height: 120 },
        table: { x: centerX - 60, y: centerY + 80, width: 120, height: 60 },
        decor: { x: centerX - 250, y: centerY - 150, width: 80, height: 80 }
      },
      // Option 2: Corner layout
      {
        rug: { x: 50, y: centerY + 20, width: 350, height: 180 },
        sofa: { x: 80, y: centerY - 80, width: 280, height: 110 },
        table: { x: 200, y: centerY + 40, width: 100, height: 50 },
        decor: { x: canvasWidth - 150, y: 50, width: 100, height: 100 }
      },
      // Option 3: Open layout
      {
        rug: { x: centerX - 180, y: centerY + 80, width: 360, height: 160 },
        sofa: { x: centerX - 120, y: centerY - 30, width: 240, height: 100 },
        table: { x: centerX - 50, y: centerY + 100, width: 100, height: 50 },
        decor: { x: 30, y: 30, width: 90, height: 90 }
      }
    ];
    
    return layouts[optionIndex]?.[type] || layouts[0][type];
  };

  const generateStagedImages = async (imageId: string): Promise<string[]> => {
    const image = uploadedImages.find(img => img.id === imageId);
    if (!image) {
      console.error('🔧 DEBUG: Image not found for ID:', imageId);
      return [];
    }
    
    console.log('🔧 DEBUG: Starting staging for image:', image.name, image.url);
    
    try {
      const stagedImages: string[] = [];
      
      // Generate 3 different staging options
      for (let optionIndex = 0; optionIndex < 3; optionIndex++) {
        console.log(`🔧 DEBUG: Processing option ${optionIndex + 1}/3`);
        setProcessingStatus(`Generating furniture set ${optionIndex + 1}/3 for ${image.roomType || image.name}...`);
        
        // Step 1: Generate furniture and rug images
        const furnitureSet = await generateFurnitureImages(selectedStyle, optionIndex);
        
        // Wait a moment for images to be ready
        await new Promise(resolve => setTimeout(resolve, 2000)); // Increased wait time
        
        setProcessingStatus(`Compositing furniture into ${image.roomType || image.name} - Option ${optionIndex + 1}...`);
        
        // Step 2: Composite furniture into room
        const stagedImage = await compositeFurnitureIntoRoom(image.url, furnitureSet, optionIndex);
        stagedImages.push(stagedImage);
        
        console.log(`🔧 DEBUG: Option ${optionIndex + 1} completed:`, stagedImage);
        
        // Update progress
        const progress = ((optionIndex + 1) / 3) * 100;
        setProcessingProgress(progress);
      }
      
      console.log('🔧 DEBUG: All staging options completed:', stagedImages);
      return stagedImages;
    } catch (error) {
      console.error('🔧 DEBUG: Error generating staged images:', error);
      return [];
    }
  };

  const handleStartStaging = async () => {
    if (selectedRooms.length === 0) return;
    
    console.log('🔧 DEBUG: Starting staging process');
    console.log('🔧 DEBUG: Selected style:', selectedStyle);
    console.log('🔧 DEBUG: Selected rooms:', selectedRooms);
    console.log('🔧 DEBUG: Available images:', uploadedImages);
    
    setCurrentStep('processing');
    setIsProcessing(true);
    setProcessingProgress(0);
    setProcessingStatus('Initializing AI staging...');

    try {
      const results: Record<string, string[]> = {};

      // Process each selected room
      for (let i = 0; i < selectedRooms.length; i++) {
        const imageId = selectedRooms[i];
        const image = uploadedImages.find(img => img.id === imageId);
        
        if (image) {
          console.log(`🔧 DEBUG: Processing image ${i + 1}/${selectedRooms.length}:`, image.name);
          setProcessingStatus(`Processing ${image.roomType || image.name} (${i + 1}/${selectedRooms.length})...`);
          
          // Generate staged images for this room
          const stagedImages = await generateStagedImages(imageId);
          
          if (stagedImages.length > 0) {
            results[imageId] = stagedImages;
            console.log(`🔧 DEBUG: Successfully staged ${stagedImages.length} options for:`, image.name);
          } else {
            console.error('🔧 DEBUG: No staged images generated for:', image.name);
          }
          
          // Update overall progress
          const overallProgress = ((i + 1) / selectedRooms.length) * 100;
          setProcessingProgress(overallProgress);
          
          // Add delay between rooms
          if (i < selectedRooms.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }

      console.log('🔧 DEBUG: Final results:', results);
      setStagedResults(results);
      setIsProcessing(false);
      setCurrentStep('results');
      setProcessingStatus('Staging complete!');
    } catch (error) {
      console.error('🔧 DEBUG: Staging error:', error);
      setIsProcessing(false);
      setProcessingStatus('Staging failed. Please try again.');
      alert('AI staging failed. Please try again.');
    }
  };

  const handleStartOver = () => {
    setCurrentStep('style');
    setSelectedRooms([]);
    setProcessingProgress(0);
    setIsProcessing(false);
    setStagedResults({});
    setProcessingStatus('');
  };

  const downloadImage = async (imageUrl: string, fileName: string) => {
    console.log('🔧 DEBUG: Downloading image:', fileName, imageUrl);
    
    try {
      // For blob URLs, we can download directly
      if (imageUrl.startsWith('blob:')) {
        console.log('🔧 DEBUG: Downloading blob URL');
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('🔧 DEBUG: Blob download initiated');
      } else {
        console.log('🔧 DEBUG: Downloading regular URL');
        // For regular URLs, fetch and download
        const response = await fetch(imageUrl);
        console.log('🔧 DEBUG: Fetch response:', response.status, response.statusText);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        console.log('🔧 DEBUG: Blob created from response:', blob.size, 'bytes');
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        console.log('🔧 DEBUG: Regular URL download completed');
      }
    } catch (error) {
      console.error('🔧 DEBUG: Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  const downloadAllImages = () => {
    Object.entries(stagedResults).forEach(([imageId, images]) => {
      const image = uploadedImages.find(img => img.id === imageId);
      const imageName = image?.roomType || image?.name || 'Room';
      images.forEach((imageUrl, index) => {
        const fileName = `${imageName.replace(/[^a-zA-Z0-9]/g, '_')}_Staged_Option_${index + 1}.jpg`;
        setTimeout(() => downloadImage(imageUrl, fileName), index * 500);
      });
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">AI Property Staging</h2>
            <p className="text-gray-600 mt-1">
              Transform your property images with AI-powered virtual staging
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${currentStep === 'style' ? 'text-blue-600' : currentStep === 'rooms' || currentStep === 'processing' || currentStep === 'results' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'style' ? 'bg-blue-100' : currentStep === 'rooms' || currentStep === 'processing' || currentStep === 'results' ? 'bg-green-100' : 'bg-gray-100'}`}>
                <i className="ri-palette-line"></i>
              </div>
              <span className="font-medium">Choose Style</span>
            </div>
            <div className="flex-1 h-px bg-gray-300"></div>
            <div className={`flex items-center space-x-2 ${currentStep === 'rooms' ? 'text-blue-600' : currentStep === 'processing' || currentStep === 'results' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'rooms' ? 'bg-blue-100' : currentStep === 'processing' || currentStep === 'results' ? 'bg-green-100' : 'bg-gray-100'}`}>
                <i className="ri-home-line"></i>
              </div>
              <span className="font-medium">Select Images</span>
            </div>
            <div className="flex-1 h-px bg-gray-300"></div>
            <div className={`flex items-center space-x-2 ${currentStep === 'processing' ? 'text-blue-600' : currentStep === 'results' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'processing' ? 'bg-blue-100' : currentStep === 'results' ? 'bg-green-100' : 'bg-gray-100'}`}>
                <i className="ri-magic-line"></i>
              </div>
              <span className="font-medium">AI Processing</span>
            </div>
            <div className="flex-1 h-px bg-gray-300"></div>
            <div className={`flex items-center space-x-2 ${currentStep === 'results' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'results' ? 'bg-green-100' : 'bg-gray-100'}`}>
                <i className="ri-check-line"></i>
              </div>
              <span className="font-medium">Results</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Style Selection */}
          {currentStep === 'style' && (
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Choose Your Staging Style</h3>
              <p className="text-gray-600 mb-6">Select the design style that best matches your target market and property type.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {stagingStyles.map((style) => (
                  <div
                    key={style.id}
                    className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                      selectedStyle === style.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => setSelectedStyle(style.id)}
                  >
                    <div className="aspect-video rounded-lg overflow-hidden mb-4">
                      <img 
                        src={style.preview} 
                        alt={style.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedStyle === style.id ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <i className={`${style.icon} ${selectedStyle === style.id ? 'text-blue-600' : 'text-gray-600'}`}></i>
                      </div>
                      <h4 className="font-bold text-gray-900">{style.name}</h4>
                    </div>
                    <p className="text-gray-600 text-sm">{style.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Room Selection */}
          {currentStep === 'rooms' && (
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Select Your Property Images to Stage</h3>
              <p className="text-gray-600 mb-6">
                Choose which of your uploaded property images you want to enhance with AI staging and furniture.
              </p>
              
              {uploadedImages && uploadedImages.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {uploadedImages.map((image) => (
                    <div
                      key={image.id}
                      className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                        selectedRooms.includes(image.id) 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => handleRoomToggle(image.id)}
                    >
                      <div className="aspect-video rounded-lg overflow-hidden mb-4">
                        <img 
                          src={image.url} 
                          alt={image.roomType || image.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-bold text-gray-900">{image.roomType || image.name}</h4>
                          <p className="text-sm text-green-600">Your Property Image</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedRooms.includes(image.id) 
                            ? 'border-blue-500 bg-blue-500' 
                            : 'border-gray-300'
                        }`}>
                          {selectedRooms.includes(image.id) && (
                            <i className="ri-check-line text-white text-sm"></i>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-image-line text-gray-400 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">No Property Images Found</h3>
                  <p className="text-gray-600 mb-6">
                    Please upload your property images first to use AI staging.
                  </p>
                  <Button onClick={onClose}>
                    Back to Dashboard
                  </Button>
                </div>
              )}

              {uploadedImages && uploadedImages.length > 0 && selectedRooms.length > 0 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-blue-800 font-medium">
                    {selectedRooms.length} property image{selectedRooms.length > 1 ? 's' : ''} selected for {stagingStyles.find(s => s.id === selectedStyle)?.name} staging
                  </p>
                  <p className="text-blue-600 text-sm mt-1">
                    Each image will generate 3 different styling options with furniture and decor
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Processing */}
          {currentStep === 'processing' && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-magic-line text-blue-600 text-3xl animate-pulse"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI is Adding Furniture & Decor</h3>
              <p className="text-gray-600 mb-4">
                Our AI is analyzing your property images and applying {stagingStyles.find(s => s.id === selectedStyle)?.name} styling with furniture, rugs, and decor...
              </p>
              
              {processingStatus && (
                <p className="text-blue-600 font-medium mb-6">{processingStatus}</p>
              )}
              
              <div className="max-w-md mx-auto">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Processing {selectedRooms.length} property images</span>
                  <span>{Math.round(processingProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${processingProgress}%` }}
                  ></div>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                {selectedRooms.slice(0, 4).map((imageId, index) => {
                  const image = uploadedImages.find(img => img.id === imageId);
                  const isProcessed = processingProgress > (index + 1) * 25;
                  return (
                    <div key={imageId} className={`p-3 rounded-lg border ${isProcessed ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                      <div className="w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center">
                        {isProcessed ? (
                          <i className="ri-check-line text-green-600"></i>
                        ) : (
                          <i className="ri-loader-4-line text-blue-600 animate-spin"></i>
                        )}
                      </div>
                      <p className="text-sm text-gray-700">{image?.roomType || image?.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Results */}
          {currentStep === 'results' && (
            <div>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-green-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Staging Complete!</h3>
                <p className="text-gray-600">
                  Your property images have been transformed with {stagingStyles.find(s => s.id === selectedStyle)?.name} styling
                </p>
              </div>

              <div className="space-y-8">
                {selectedRooms.map((imageId) => {
                  const image = uploadedImages.find(img => img.id === imageId);
                  const stagedImages = stagedResults[imageId] || [];
                  
                  return (
                    <div key={imageId} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4 bg-gray-50 border-b">
                        <h4 className="font-bold text-gray-900 text-lg">{image?.roomType || image?.name}</h4>
                        <p className="text-sm text-gray-600">3 styling options with furniture and decor applied to your property image</p>
                      </div>
                      
                      <div className="p-6">
                        {/* Before Image */}
                        <div className="mb-6">
                          <p className="text-sm font-medium text-gray-700 mb-2">Your Original Property Image</p>
                          <div className="rounded-lg overflow-hidden">
                            <img src={image?.url} alt={`${image?.roomType || image?.name} original`} className="w-full h-48 object-cover" />
                          </div>
                        </div>

                        {/* After Images - 3 Options */}
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-4">AI Staged Versions</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {stagedImages.map((imageUrl, index) => (
                              <div key={index} className="relative">
                                <div className="rounded-lg overflow-hidden">
                                  <img 
                                    src={imageUrl} 
                                    alt={`${image?.roomType || image?.name} staged option ${index + 1}`} 
                                    className="w-full h-48 object-cover"
                                  />
                                </div>
                                <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                  Option {index + 1}
                                </div>
                                <div className="mt-2 flex justify-center">
                                  <Button 
                                    className="text-sm px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap"
                                    onClick={() => {
                                      const imageName = image?.roomType || image?.name || 'Room';
                                      const fileName = `${imageName.replace(/[^a-zA-Z0-9]/g, '_')}_Staged_Option_${index + 1}.jpg`;
                                      downloadImage(imageUrl, fileName);
                                    }}
                                  >
                                    <i className="ri-download-line mr-1"></i>
                                    Download
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-xl">
                <h4 className="font-bold text-blue-900 mb-2">What's Next?</h4>
                <ul className="text-blue-800 space-y-1">
                  <li>• Download your favorite staged versions</li>
                  <li>• Add to your property listing</li>
                  <li>• Share with potential buyers</li>
                  <li>• Track engagement and interest</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {currentStep === 'style' && 'Step 1 of 4: Choose your preferred staging style'}
            {currentStep === 'rooms' && `Step 2 of 4: ${selectedRooms.length} property image${selectedRooms.length !== 1 ? 's' : ''} selected`}
            {currentStep === 'processing' && 'Step 3 of 4: AI processing your property images...'}
            {currentStep === 'results' && 'Step 4 of 4: Staging complete!'}
          </div>
          
          <div className="flex space-x-3">
            {currentStep === 'style' && (
              <>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={() => setCurrentStep('rooms')}>
                  Next: Select Images
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </>
            )}
            
            {currentStep === 'rooms' && (
              <>
                <Button variant="outline" onClick={() => setCurrentStep('style')}>
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back
                </Button>
                <Button 
                  onClick={handleStartStaging}
                  disabled={selectedRooms.length === 0}
                >
                  <i className="ri-magic-line mr-2"></i>
                  Start AI Staging ({selectedRooms.length})
                </Button>
              </>
            )}
            
            {currentStep === 'processing' && (
              <Button disabled>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Processing...
              </Button>
            )}
            
            {currentStep === 'results' && (
              <>
                <Button variant="outline" onClick={handleStartOver}>
                  <i className="ri-refresh-line mr-2"></i>
                  Stage More Images
                </Button>
                <Button onClick={() => {
                  downloadAllImages();
                  onClose();
                }}>
                  <i className="ri-download-line mr-2"></i>
                  Download All
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}